"use client";

import { useFormik } from "formik";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";
import { useMember } from "../context/MemberContext";
import { useTagManager } from "../context/TagManagerContext";
import { CookieConsent } from "../models/consent";

import Button from "./Button";
import ConsentSwitch from "./member-settings/consents/ConsentSwitch";

const CookieBanner: React.FC = () => {
  const { t } = useTranslation("common");
  const [openCustom, setOpenCustom] = useState(false);
  const [displayed, setDisplayed] = useState(false);
  const isServer = typeof window === "undefined";
  const { member, changeMemberConsents } = useMember();
  const { sendMarketingConsent } = useTagManager();
  const { user } = useAuth();

  useEffect(() => {
    if (isServer || (user && !member)) return;
    const sessionConsent = JSON.parse(localStorage.getItem("cookies-consent"));
    if (!sessionConsent && !member && !user) {
      setDisplayed(true);
      return;
    }

    if (!member) {
      return;
    }

    const consents:CookieConsent = member.consents?.filter((c) => c.type === "COOKIE").reduce((acc, curr) => {
      if (curr.purposeKey === "necessary-cookies") return { ...acc, necessary: curr.value ?? sessionConsent?.necessary ?? null };
      if (curr.purposeKey === "preferences-cookies") return { ...acc, preferences: curr.value ?? sessionConsent?.preferences ?? null };
      if (curr.purposeKey === "statistics-cookies") return { ...acc, statistics: curr.value ?? sessionConsent?.statistics ?? null };
      if (curr.purposeKey === "marketing-cookies") return { ...acc, marketing: curr.value ?? sessionConsent?.marketing ?? null };
      return acc;
    }, {
      necessary: null, preferences: null, statistics: null, marketing: null,
    });

    if (Object.values(consents).some((v) => v === null)) {
      setDisplayed(true);
    } else {
      setDisplayed(false);
      submitMemberConsent(consents);
    }
  }, [member, user, isServer]);

  const submitMemberConsent = (form: CookieConsent) => {
    localStorage.setItem("cookies-consent", JSON.stringify(form));
    sendMarketingConsent(form.marketing);

    const consentsToUpdate = member?.consents?.map((c) => {
      if (c.purposeKey === "necessary-cookies" && form.necessary !== c.value) return { purposeUid: c.purposeUid, value: form.necessary };
      if (c.purposeKey === "preferences-cookies" && form.preferences !== c.value) return { purposeUid: c.purposeUid, value: form.preferences };
      if (c.purposeKey === "statistics-cookies" && form.statistics !== c.value) return { purposeUid: c.purposeUid, value: form.statistics };
      if (c.purposeKey === "marketing-cookies" && form.marketing !== c.value) return { purposeUid: c.purposeUid, value: form.marketing };
      return null;
    }).filter((c) => c !== null);

    changeMemberConsents(consentsToUpdate);
  };

  const initialValues: CookieConsent = {
    necessary: true,
    preferences: true,
    statistics: true,
    marketing: true,
  };

  const formik = useFormik<CookieConsent>({
    initialValues,
    onSubmit: ((form) => {
      submitMemberConsent(form);
      setDisplayed(false);
    }),
  });

  if (!displayed) return <></>;

  return (
    <div className="fixed h-full w-full z-[1000] top-0 left-0">
      <div className="h-full w-full bg-black opacity-40" />
      <div className="flex-row w-full bg-white fixed bottom-0 p-4 lg:p-0 lg:py-16" data-pw="cookiesDisclaimer">
        <div className="wrapper space-y-4 max-w-screen-lg">
          <div className="font-semibold text-xl">{t("cookies.disclaimerTitle")}</div>
          <div>{t("cookies.disclaimer")}</div>
          <div className="space-y-4 lg:space-y-0 lg:space-x-4 flex lg:flex-row flex-col justify-end">
            {!openCustom && (
              <Button className="w-full lg:w-auto" size="medium" render="simple" data-pw="cookiesSettings" onClick={() => setOpenCustom(!openCustom)}>{t("cookies.customize")}</Button>
            )}
            <Button size="medium" className="w-full lg:w-auto" onClick={() => formik.submitForm()} data-pw="cookiesAcceptance">
              {formik.dirty ? t("cookies.acceptSelection") : t("cookies.acceptAll")}
            </Button>
          </div>
          {openCustom && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:justify-items-center mt-2">
              <ConsentSwitch consentKey="cookies.necessary" value={formik.values.necessary} onChange={(v: boolean) => formik.setFieldValue("necessary", v)} disabled />
              <ConsentSwitch consentKey="cookies.preferences" value={formik.values.preferences} onChange={(v: boolean) => formik.setFieldValue("preferences", v)} data-pw="preferences" />
              <ConsentSwitch consentKey="cookies.statistics" value={formik.values.statistics} onChange={(v: boolean) => formik.setFieldValue("statistics", v)} data-pw="statistics" />
              <ConsentSwitch consentKey="cookies.marketing" value={formik.values.marketing} onChange={(v: boolean) => formik.setFieldValue("marketing", v)} data-pw="marketing" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
